import React, { useEffect, useState } from "react";
import "./Products.css";
import Swal from 'sweetalert2';
import * as XLSX from "xlsx";
import CryptoJS from 'crypto-js';

const Products = () => {
  const [selectedFilters, setSelectedFilters] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const [totalProducts, setTotalProducts] = useState(0);

  const secretKey = process.env.REACT_APP_SECRET_KEY;

  const getBackendUrl = () => {
    return window.location.hostname === 'localhost'
      ? process.env.REACT_APP_BACKEND_URL_LOCAL
      : process.env.REACT_APP_BACKEND_URL_PRODUCTION;
  };

  const [products, setProducts] = useState([]);
  const [errorMessage, setErrorMessage] = useState("");
  const [searchPerformed, setSearchPerformed] = useState(false);

  const toggleFilter = (filterCategory, value) => {
    setSelectedFilters((prevFilters) => {
      let formattedValue = value; // Default behavior

      // Fix for H&C
      if (filterCategory === "HAndC") {
        formattedValue = value === "YES" ? encodeURIComponent("H&C") : ""; // Encode to prevent splitting
      } else if (["bgm", "3EX"].includes(filterCategory)) {
        formattedValue = value === "YES" ? filterCategory.toUpperCase() : "";
      }

      const categoryValues = prevFilters[filterCategory] || [];
      if (categoryValues.includes(formattedValue)) {
        return {
          ...prevFilters,
          [filterCategory]: categoryValues.filter((v) => v !== formattedValue),
        };
      } else {
        return {
          ...prevFilters,
          [filterCategory]: [...categoryValues, formattedValue],
        };
      }
    });
  };

  const encryptPayload = (payload, secretKey) => {
    return CryptoJS.AES.encrypt(JSON.stringify(payload), secretKey).toString();
  };

  const handleSubmit = async () => {
    try {
      // Check if no filters are selected
      if (Object.keys(selectedFilters).length === 0 || Object.values(selectedFilters).every((v) => v.length === 0)) {
        setProducts([]);
        Swal.fire({
          icon: "error",
          title: "No items selected for search",
          text: "Please select at least one filter before searching.",
        });
        return;
      }

      setSearchPerformed(false);
      setErrorMessage("");

      // Filter out empty filters before creating the query
      const filteredFilters = Object.entries(selectedFilters).filter(
        ([_, values]) => values.length > 0
      );

      const query = filteredFilters
        .map(([key, values]) => `${key}=${values.join(",")}`)
        .join("&");

      const encryptedQuery = encryptPayload(query, secretKey);

      const response = await fetch(`${getBackendUrl()}/api/product/products`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ encryptedQuery, page: currentPage, limit: itemsPerPage }),

      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || "An error occurred.");
      }

      if (data.products && data.products.length > 0) {
        setProducts(data.products);
        setTotalProducts(data.totalProducts);
        setSearchPerformed(true);
      } else {
        Swal.fire({
          icon: "info",
          title: "No products found",
          text: data.message || "No products match the selected filters. Please try different criteria.",
        });
        setProducts([]);
        setSearchPerformed(true);
      }

    } catch (error) {
      setProducts([]);
      setSearchPerformed(true);
      setErrorMessage(error.message);

      Swal.fire({
        icon: "error",
        title: "Search Error",
        text: error.message || "Something went wrong, please try again later.",
      });
    }
  };

  const exportToExcel = () => {
    if (products.length === 0) return;

    // Filter out unwanted fields (_id, adminId, __v)
    const filteredProducts = products.map((product) => {
      const { _id, adminId, __v, ...filteredProduct } = product;
      return filteredProduct;
    });

    const worksheet = XLSX.utils.json_to_sheet(filteredProducts);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Search Results");

    XLSX.writeFile(workbook, "search_results.xlsx");
  };


  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };
  const handleItemsPerPageChange = (event) => {
    setItemsPerPage(Number(event.target.value));
    setCurrentPage(1); // Reset to first page when changing items per page
  };
  useEffect(() => {
    if (searchPerformed) {
      handleSubmit();
    }
    // eslint-disable-next-line
  }, [currentPage, itemsPerPage]);

  const totalPages = Math.ceil(totalProducts / itemsPerPage);


  return (
    <div className="products-container">
      <h1 className="title">Stock List</h1>
      <div className="min-h-screen bg-gray-100 p-4">
      <div className="filter-grid">
        <div className="filter-category">
          <h3>Shape</h3>
          <div className="filter-options">
            {["BR", "MQ", "PS", "OV", "EM", "HS", "PR", "BGT", "RAD", "CU", "TRI", "OTH"].map((shape) => (
              <label key={shape} className="filter-option">
                <input
                  type="checkbox"
                  onChange={() => toggleFilter("shape", shape)}
                />
                {shape}
              </label>
            ))}
          </div>
        </div>
        <div className="filter-category">
          <h3>Carat</h3>
          <div className="filter-options">
            {[0.1, 0.15, 0.2, 0.25, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9, 1, 2, 3, 4, 5, 10].map((value) => (
              <label key={value} className="filter-option">
                <input
                  type="checkbox"
                  onChange={() => toggleFilter("carat", value)}
                />
                {`${value}ct`}
              </label>
            ))}
          </div>
        </div>
        <div className="filter-category">
          <h3>Color</h3>
          <div className="filter-options">
            {["D", "E", "F", "G", "H", "I", "J", "K", "L", "M", "N", "O-P", "Q-R", "S-T", "U-V", "W-X", "Y-Z", "VLY", "LY", "Pink", "YL", "BL", "OTH"].map((color) => (
              <label key={color} className="filter-option">
                <input
                  type="checkbox"
                  onChange={() => toggleFilter("color", color)}
                />
                {color}
              </label>
            ))}
          </div>
        </div>
        <div className="filter-category">
          <h3>Clarity</h3>
          <div className="filter-options">
            {["FL", "IF", "VVS1", "VVS2", "VS1", "VS2", "SI1", "SI2", "I1", "I2", "I3"].map((clarity) => (
              <label key={clarity} className="filter-option">
                <input
                  type="checkbox"
                  onChange={() => toggleFilter("clarity", clarity)}
                />
                {clarity}
              </label>
            ))}
          </div>
        </div>
        <div className="filter-category">
          <h3>Cut</h3>
          <div className="filter-options">
            {["EX", "VG", "G", "F", "P"].map((cut) => (
              <label key={cut} className="filter-option">
                <input
                  type="checkbox"
                  onChange={() => toggleFilter("cut", cut)}
                />
                {cut}
              </label>
            ))}
          </div>
        </div>
        <div className="filter-category">
          <h3>Polish</h3>
          <div className="filter-options">
            {["EX", "VG", "G", "F", "P"].map((polish) => (
              <label key={polish} className="filter-option">
                <input
                  type="checkbox"
                  onChange={() => toggleFilter("polish", polish)}
                />
                {polish}
              </label>
            ))}
          </div>
        </div>
        <div className="filter-category">
          <h3>Symmetry</h3>
          <div className="filter-options">
            {["EX", "VG", "G", "F", "P"].map((symmetry) => (
              <label key={symmetry} className="filter-option">
                <input
                  type="checkbox"
                  onChange={() => toggleFilter("symmetry", symmetry)}
                />
                {symmetry}
              </label>
            ))}
          </div>
        </div>
        <div className="filter-category">
          <h3>Fluorescence</h3>
          <div className="filter-options">
            {["NON", "FNT", "MED", "STG", "VST"].map((fluorescence) => (
              <label key={fluorescence} className="filter-option">
                <input
                  type="checkbox"
                  onChange={() => toggleFilter("fluorescence", fluorescence)}
                />
                {fluorescence}
              </label>
            ))}
          </div>
        </div>
        <div className="filter-category">
          <h3>Lab</h3>
          <div className="filter-options">
            {["GIA", "CGL", "GGS", "OTH"].map((lab) => (
              <label key={lab} className="filter-option">
                <input
                  type="checkbox"
                  onChange={() => toggleFilter("lab", lab)}
                />
                {lab}
              </label>
            ))}
          </div>
        </div>
        <div className="filter-category">
          <h3>BGM</h3>
          <div className="filter-options">
            {["YES", "NO"].map((option) => (
              <label key={option} className="filter-option">
                <input
                  type="checkbox"
                  onChange={() => toggleFilter("bgm", option)}
                />
                {option}
              </label>
            ))}
          </div>
        </div>
        <div className="filter-category">
          <h3>H&C</h3>
          <div className="filter-options">
            {["YES", "NO"].map((option) => (
              <label key={option} className="filter-option">
                <input
                  type="checkbox"
                  onChange={() => toggleFilter("HAndC", option)}
                />
                {option}
              </label>
            ))}
          </div>
        </div>
        <div className="filter-category">
          <h3>3EX</h3>
          <div className="filter-options">
            {["YES", "NO"].map((option) => (
              <label key={option} className="filter-option">
                <input
                  type="checkbox"
                  onChange={() => toggleFilter("3EX", option)}
                />
                {option}
              </label>
            ))}
          </div>
        </div>
      </div>
    </div>
      <button className="filter-button" onClick={handleSubmit}>
        Search
      </button>

      {searchPerformed && (
        <div className="product-tables">
          {products.length > 0 ? (
            <div>
              <h2>Search Results</h2>
              <p>Results are sorted and grouped as per the selected filters.</p>
              <div className="pagination-container">
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                >
                  Previous
                </button>
                <span>{`Page ${currentPage} of ${totalPages}`}</span>
                {console.log("total", totalPages)}
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                >
                  Next
                </button>
                <select value={itemsPerPage} onChange={handleItemsPerPageChange}>
                  {[20, 40, 60, 80, 100].map((size) => (
                    <option key={size} value={size}>
                      Show {size}
                    </option>
                  ))}
                </select>
              </div>
              <div className="fetched-tables-container">
                <table className="fetched-tables">
                  <thead>
                    <tr>
                      <th>Sr No</th> {/* New Serial Number Column */}
                      {Object.keys(products[0] || {})
                        .filter((key) => !["_id", "adminId", "__v", "isSold", "uploadedAt"].includes(key))
                        .map((key, index) => (
                          <th key={index}>{key}</th>
                        ))}
                    </tr>
                  </thead>
                  <tbody>
                    {products.map((row, rowIndex) => (
                      <tr key={rowIndex}>
                        <td>{rowIndex + 1}</td>
                        {Object.entries(row)
                          .filter(([key]) => !["_id", "adminId", "__v", "isSold", "uploadedAt"].includes(key))
                          .map(([key, value], valueIndex) => {
                            let formattedValue = value;

                            // Check if value is a number and has decimals before formatting
                            if ((key === "Price/ct" || key === "Amount") && !Number.isInteger(value)) {
                              formattedValue = parseFloat(value).toFixed(4);
                            }

                            return <td key={valueIndex}>{formattedValue}</td>;
                          })}
                      </tr>
                    ))}
                  </tbody>

                </table>
              </div>
              <div className="download-button-container">
                <button className="download-button" onClick={exportToExcel}>
                  Download as Excel
                </button>
              </div>
            </div>
          ) : (
            <p>No products found for the selected filters.</p>
          )}
        </div>
      )}
      {errorMessage && <p className="error-message">{errorMessage}</p>}


    </div>
  );
};

export default Products;
